/**
 * DockMessageExtras (TASK-713) — artifacts, sources, and app-control actions
 * attached to a dock transcript message. The action dispatch (permission gate,
 * navigate, chat:* CustomEvents) is ported from the deleted chat/ChatMessage.
 */

import React from 'react';
import { ExternalLink, MousePointerClick } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAccount } from '../../contexts/AccountContext';
import type { Artifact, ChatAction, ChatSource } from '../../types/chat.types';
import ArtifactRenderer from './artifacts/ArtifactRenderer';

export interface DockMessageExtrasProps {
  artifacts?: Artifact[];
  actions?: ChatAction[];
  sources?: ChatSource[];
  /**
   * TASK-778: ask the copilot a follow-up. A `query` action IS a message — it
   * belongs in the dock's one send pipeline, the same way MeetingSideCards'
   * `onRevise` does. Without it the chip has nowhere to go.
   */
  onQuery?: (text: string) => void;
}

/**
 * TASK-778: can this surface actually PERFORM the action?
 *
 * Every non-navigate type used to be dispatched as a `chat:{type}` CustomEvent,
 * and **nothing in the codebase has ever listened for one** — `chat:query`,
 * `chat:filter`, `chat:highlight`, `chat:modal` and `chat:export` all have zero
 * `addEventListener` call sites. So the advisor clicked "What meetings are
 * scheduled?" and the click went nowhere, with no feedback at all.
 *
 * `query` is now answered here. The other four have no consumer anywhere, so
 * they render disabled with the reason rather than lying about being clickable
 * — the same rule TASK-768 applied to the canvas starter chips: a visible
 * control that cannot fire is worse than one that is not offered.
 */
const performability = (
  action: ChatAction,
  canQuery: boolean
): { can: true } | { can: false; reason: string } => {
  switch (action.type) {
    case 'navigate':
      return typeof action.payload.route === 'string' && action.payload.route
        ? { can: true }
        : { can: false, reason: 'This suggestion has no destination attached.' };
    case 'query':
      return canQuery
        ? { can: true }
        : { can: false, reason: 'Asking a follow-up is not available here.' };
    default:
      return { can: false, reason: `“${action.type}” actions are not supported yet.` };
  }
};

export const DockMessageExtras: React.FC<DockMessageExtrasProps> = ({
  artifacts,
  actions,
  sources,
  onQuery,
}) => {
  const navigate = useNavigate();
  const { hasPermission } = useAccount();

  const handleActionClick = (action: ChatAction) => {
    if (action.requires_permission && !hasPermission(action.requires_permission)) {
      console.warn(`Missing permission for action: ${action.requires_permission}`);
      return;
    }
    if (action.type === 'navigate') {
      if (action.payload.route) navigate(action.payload.route as string);
      return;
    }
    if (action.type === 'query') {
      // The backend sends the question in `payload.query`; the label is the
      // same text today, and the fallback keeps the chip working if that
      // changes rather than sending nothing.
      const text =
        typeof action.payload.query === 'string' && action.payload.query
          ? action.payload.query
          : action.label;
      onQuery?.(text);
    }
    // Everything else is unreachable: those chips render disabled.
  };

  if (!artifacts?.length && !actions?.length && !sources?.length) return null;

  return (
    <div className="ml-9 space-y-2">
      {artifacts?.map((artifact, index) => (
        <ArtifactRenderer key={artifact.id || `artifact-${index}`} artifact={artifact} />
      ))}
      {sources && sources.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {sources.map((source, index) => (
            <span
              key={source.id || `source-${index}`}
              className="inline-flex items-center gap-1 rounded-md bg-accent px-2 py-1 text-xs text-muted-foreground"
              title={source.excerpt}
            >
              <ExternalLink className="h-3 w-3" />
              {source.title}
            </span>
          ))}
        </div>
      )}
      {actions && actions.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {actions.map((action, index) => {
            const permitted =
              !action.requires_permission || hasPermission(action.requires_permission);
            const capability = performability(action, !!onQuery);
            const enabled = permitted && capability.can;
            const reason = !permitted
              ? `Requires ${action.requires_permission} permission`
              : capability.can
                ? action.label
                : capability.reason;
            return (
              <button
                key={action.id || `action-${index}`}
                onClick={() => handleActionClick(action)}
                disabled={!enabled}
                data-testid="dock-action-chip"
                data-action-type={action.type}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                  enabled
                    ? 'border-border bg-background text-foreground hover:bg-accent'
                    : 'cursor-not-allowed border-border bg-muted text-muted-foreground'
                }`}
                title={reason}
              >
                <MousePointerClick className="h-3 w-3" />
                {action.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
